import { useState } from 'react';
import { asistenciaService } from '@/services/AsistenciaService';
import { RegistroEntrenoDto, DiaEntrenamiento, AsistenciaJugador } from '@/types/asistencia';
import { JugadorDto } from '@/types/jugador';

/**
 * Hook para gestionar la asistencia a entrenamientos
 */
export const useAsistencia = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registros, setRegistros] = useState<RegistroEntrenoDto[]>([]);

  /**
   * Obtiene los días de entrenamiento de la semana actual (Lunes a Viernes)
   */
  const obtenerDiasEntrenamiento = (): DiaEntrenamiento[] => {
    const hoy = new Date();
    const inicioSemana = new Date(hoy);
    
    // Obtener el lunes de la semana actual
    const diaSemana = hoy.getDay();
    const diasHastaLunes = diaSemana === 0 ? -6 : 1 - diaSemana;
    inicioSemana.setDate(hoy.getDate() + diasHastaLunes);
    
    const dias: DiaEntrenamiento[] = [];
    const nombresDias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    
    // Generar días de Lunes (0) a Viernes (4)
    for (let i = 0; i < 5; i++) {
      const fecha = new Date(inicioSemana);
      fecha.setDate(inicioSemana.getDate() + i);
      
      dias.push({
        fecha: fecha.toISOString().split('T')[0],
        dia: nombresDias[i],
        numero: fecha.getDate(),
        tieneRegistros: false
      });
    }
    
    return dias;
  };

  /**
   * Obtiene los registros de asistencia para una fecha específica
   */
  const obtenerRegistrosPorFecha = async (fecha: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await asistenciaService.obtenerRegistrosPorFecha(fecha);
      if (response.success) {
        setRegistros(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error al obtener registros de asistencia');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Guarda los registros de asistencia
   */
  const guardarAsistencia = async (fecha: string, asistencias: AsistenciaJugador[]) => {
    setLoading(true);
    setError(null);
    
    try {
      const registrosMap = asistencias.reduce((acc, asistencia) => {
        acc[asistencia.jugadorId] = asistencia.asistio;
        return acc;
      }, {} as Record<number, boolean>);
      
      const response = await asistenciaService.guardarRegistrosAsistencia(fecha, registrosMap);
      if (response.success) {
        setRegistros(response.data);
        return true;
      } else {
        setError(response.message);
        return false;
      }
    } catch (err) {
      setError('Error al guardar asistencia');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Convierte jugadores y registros existentes en AsistenciaJugador
   */
  const prepararAsistenciaJugadores = (jugadores: JugadorDto[], registrosExistentes: RegistroEntrenoDto[]): AsistenciaJugador[] => {
    return jugadores.map(jugador => {
      const registroExistente = registrosExistentes.find(r => r.jugadorId === jugador.id);
      return {
        jugadorId: jugador.id,
        nombre: jugador.nombre,
        apellido: jugador.apellido,
        asistio: registroExistente?.asistio ?? false
      };
    });
  };

  return {
    loading,
    error,
    registros,
    obtenerDiasEntrenamiento,
    obtenerRegistrosPorFecha,
    guardarAsistencia,
    prepararAsistenciaJugadores
  };
};